import type { QgammonLiveMatch } from '@/types/qgammon';
import { modeOfTimeControl, type RankedQueueMode } from '@/lib/qgammon/queueMode';

/**
 * LA SCHEDA «LIVE» DEL BACKGAMMON — cosa diventa una riga, senza React (un banco la esegue).
 *
 * Fonte: `GET /matches/live` del gameserver (v. `QgammonLiveMatch`). Richiesta di Luca I-208 (26/09):
 * «in sezione hub backgammon voglio poter vedere le partite live che si stanno giocando in questo
 * momento». Disegno approvato da tma-orcb il 26/09 12:03Z.
 */

export interface RigaLive {
  readonly match: QgammonLiveMatch;
  /**
   * Il colore con cui IO gioco questa partita, o `null` se non ci gioco.
   *
   * 🔴 Una partita mia non si GUARDA, si RIPRENDE: il gameserver rifiuta `match:spectate` a un
   * partecipante col Bearer («use match:join»), e da spettatore anonimo vedrei il mio tavolo senza
   * poterci giocare — mentre il mio orologio corre.
   */
  readonly mioColore: 'W' | 'B' | null;
}

/**
 * Le righe della scheda: la MIA partita in testa, poi le altre dalla piu' recente. Un `startedAt`
 * illeggibile va in fondo invece di rompere l'ordine. `myUserId` e' l'id QGammon (`/auth/me`
 * `user.id`, la stessa tabella da cui il gameserver prende `slot.userId`); `null` finche' non si sa.
 */
export function righeLive(
  matches: readonly QgammonLiveMatch[],
  myUserId: string | null | undefined,
): RigaLive[] {
  const righe = matches.map((match): RigaLive => ({ match, mioColore: mioColoreIn(match, myUserId) }));
  const t = (m: QgammonLiveMatch) => {
    const ms = Date.parse(m.startedAt);
    return Number.isNaN(ms) ? -Infinity : ms;
  };
  return righe.sort((a, b) => {
    const mieA = a.mioColore !== null ? 1 : 0;
    const mieB = b.mioColore !== null ? 1 : 0;
    if (mieA !== mieB) return mieB - mieA;
    return t(b.match) - t(a.match);
  });
}

function mioColoreIn(m: QgammonLiveMatch, myUserId: string | null | undefined): 'W' | 'B' | null {
  if (!myUserId) return null;
  if (m.white.userId === myUserId) return 'W';
  if (m.black.userId === myUserId) return 'B';
  return null;
}

/** Da quanti minuti interi e' cominciata; `null` se `startedAt` non si legge. Mai negativo (orologi sfasati). */
export function minutiDa(startedAt: string, nowMs: number): number | null {
  const ms = Date.parse(startedAt);
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor((nowMs - ms) / 60_000));
}

/**
 * Con o senza orologio — e NIENT'ALTRO. ⛔ Nessuna DURATA («blitz», «rapid», 180/10): L-722, v.
 * `queueMode.ts`. Si riusa lo stesso lettore della coda, cosi' le due superfici non divergono.
 */
export function orologioDi(m: QgammonLiveMatch): RankedQueueMode | null {
  return modeOfTimeControl(m.timeControl);
}
